// Typed invocations of the four Edge Functions. Responses are validated with the zod
// schemas from @horizon/types; failures are normalised to ErreurHorizon.
import {
  echeanceTransitionOutput,
  exportPdfOutput,
  genererOccurrencesOutput,
  simulerOutput,
  type ActionTransition,
  type EcheanceTransitionOutput,
  type ExportPdfOutput,
  type GenererOccurrencesOutput,
  type SimulerOutput,
} from '@horizon/types';
import type { ZodType } from 'zod';
import { getClient } from './client.js';
import { erreurLocale, toErreur } from './erreurs.js';

async function invoquer<T>(
  nom: string,
  corps: Record<string, unknown>,
  schema: ZodType<T>,
): Promise<T> {
  const { data, error } = await getClient().functions.invoke(nom, { body: corps });
  if (error !== null) {
    // functions.invoke wraps the HTTP error; the body carries our envelope.
    const contexte = (error as { context?: { body?: unknown } }).context?.body;
    throw toErreur(contexte ?? error);
  }
  const analyse = schema.safeParse(data);
  if (!analyse.success) {
    throw erreurLocale('ERREUR_INTERNE', `Invalid response from ${nom}`, analyse.error.format());
  }
  return analyse.data;
}

/** Recompute the three scenarios and rewrite the projections (RG-03). */
export function simuler(simulationId: string): Promise<SimulerOutput> {
  return invoquer('simuler', { simulation_id: simulationId }, simulerOutput);
}

/** Apply an echeance state transition (RG-11). */
export function transitionEcheance(
  echeanceId: string,
  action: ActionTransition,
  nouvelleDate?: string,
): Promise<EcheanceTransitionOutput> {
  const corps: Record<string, unknown> = { echeance_id: echeanceId, action };
  if (nouvelleDate !== undefined) {
    corps.nouvelle_date = nouvelleDate;
  }
  return invoquer('echeance-transition', corps, echeanceTransitionOutput);
}

/** Generate the PDF report; throws REGLE_VIOLEE if the KYC is expired (RG-14). */
export function exporterPdf(simulationId: string): Promise<ExportPdfOutput> {
  return invoquer('export-pdf', { simulation_id: simulationId }, exportPdfOutput);
}

/** Trigger the monthly occurrence generation (RG-12); usually scheduled. */
export function genererOccurrences(): Promise<GenererOccurrencesOutput> {
  return invoquer('generer-occurrences', {}, genererOccurrencesOutput);
}
